import {
  Avatar,
  Link,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import {
  getHighlightValue,
  getOpenSearchPostBodyAttribute,
  getOpenSearchPostTitle,
  getOpenSearchPostUrl,
} from '../hitHelpers';
import { OpenSearchHit } from '../types';
import { OpenSearchText } from './OpenSearchText';

const ACTION: Record<string, string> = {
  BLOG: 'posted an article',
  POLL: 'posted a poll',
  POST: 'posted',
  SIMPLE_POST: 'posted',
  COMMENT: 'replied',
  QUESTION: 'asked a question',
  ANSWER: 'posted an answer',
};

const titleAttribute = (hit: OpenSearchHit): string => {
  switch (hit.postType) {
    case 'BLOG':
      return 'blog.title';
    case 'POLL':
      return 'poll.title';
    case 'QUESTION':
      return 'question.title';
    default:
      return 'title';
  }
};

export const OpenSearchPost: React.FC<{ hit: OpenSearchHit }> = ({ hit }) => {
  const displayName = hit.displayName || hit.authorDisplayName || '';
  const groupId = hit.group?._id || hit.groupId;
  const groupName = hit.group?.name || hit.groupName;
  const organizationId = hit.organization?._id || hit.organizationId;
  const organizationName = hit.organization?.name || hit.organizationName;
  const title =
    getHighlightValue(hit, titleAttribute(hit), '_highlightResult') ||
    getOpenSearchPostTitle(hit);
  const body = getHighlightValue(
    hit,
    getOpenSearchPostBodyAttribute(hit),
    '_snippetResult'
  );

  return (
    <LinkBox
      as={Stack}
      spacing="1"
      p="2"
      bg="white"
      _hover={{ bg: 'gray.100' }}
      borderRadius="xl"
      cursor="pointer"
    >
      <Stack direction="row" alignItems="center">
        <Avatar size="xs" name={displayName} />
        <Text textStyle="compact" color="gray.600">
          {hit.authorId ? (
            <Link as={NextLink} href={`/members/${hit.authorId}`}>
              {displayName}
            </Link>
          ) : (
            displayName
          )}{' '}
          {ACTION[hit.postType || 'POST'] || 'posted'} in{' '}
          {groupId && groupName ? (
            <Link as={NextLink} href={`/community/groups/${groupId}/activity`}>
              {groupName}
            </Link>
          ) : organizationId && organizationName ? (
            <Link as={NextLink} href={`/companies/${organizationId}`}>
              {organizationName}
            </Link>
          ) : null}
        </Text>
      </Stack>

      <Stack spacing="0">
        <LinkOverlay as={NextLink} href={getOpenSearchPostUrl(hit)}>
          <Text noOfLines={1} textStyle="bodyStrong">
            <OpenSearchText value={title} />
          </Text>
        </LinkOverlay>

        {body && (
          <Text noOfLines={2} textStyle="compact" color="gray.600">
            <OpenSearchText value={body} />
          </Text>
        )}
      </Stack>
    </LinkBox>
  );
};
